/**
 * Unified Logger for Support App
 *
 * Simple import, no injection required.
 *
 * Usage:
 *   import { log } from '@app/_services/logger/log';
 *
 *   // General logs
 *   log.debug('AuthService', 'Checking token', { token });
 *   log.info('AuthService', 'User authenticated', { userId });
 *   log.warn('AuthService', 'Token expiring soon');
 *   log.error('AuthService', 'Auth failed', error);
 *
 *   // Function calls
 *   log.fn('getUsers', 'CALLING');
 *   log.fn('getUsers', 'SUCCESS', result, 567);
 *   log.fn('getUsers', 'ERROR', error);
 */

import {
  LogLevel,
  FnStatus,
  LEVEL_CONFIG,
  LEVEL_HIERARCHY,
  LOG_STORAGE_KEY,
} from './log.types';

// ============================================================================
// STATE
// ============================================================================

// Default log level (can be changed via console command)
let currentLevel: LogLevel = 'debug';

// Load settings from localStorage
function loadSettings(): void {
  try {
    const stored = localStorage.getItem(LOG_STORAGE_KEY);
    if (stored) {
      const settings = JSON.parse(stored);
      if (settings.level && LEVEL_HIERARCHY[settings.level as LogLevel] !== undefined) {
        currentLevel = settings.level as LogLevel;
      }
    }
  } catch {
    // Ignore storage errors
  }
}

// Initialize on load
loadSettings();

// ============================================================================
// HELPERS
// ============================================================================

function shouldShowLevel(level: LogLevel): boolean {
  if (currentLevel === 'off') return false;
  return LEVEL_HIERARCHY[level] >= LEVEL_HIERARCHY[currentLevel];
}

function formatDuration(ms: number): string {
  if (ms < 1) return '<1ms';
  if (ms < 1000) return `${Math.round(ms)}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

function buildHeader(level: Exclude<LogLevel, 'off'>, tag: string, message: string): string {
  const config = LEVEL_CONFIG[level];
  return `${config.emoji} ${config.label} [${tag}] ${message}`;
}

function getStyle(level: Exclude<LogLevel, 'off'>): string {
  const config = LEVEL_CONFIG[level];
  return `color: ${config.color}; font-weight: bold`;
}

// ============================================================================
// LOGGER OBJECT
// ============================================================================

export const log = {
  /**
   * Debug level log
   * @param tag - Source identifier (e.g., service name)
   * @param message - Log message
   * @param data - Optional data
   */
  debug(tag: string, message: string, data?: unknown): void {
    if (!shouldShowLevel('debug')) return;
    const header = buildHeader('debug', tag, message);
    if (data !== undefined) {
      console.debug(`%c${header}`, getStyle('debug'), data);
    } else {
      console.debug(`%c${header}`, getStyle('debug'));
    }
  },

  /**
   * Info level log
   * @param tag - Source identifier (e.g., service name)
   * @param message - Log message
   * @param data - Optional data
   */
  info(tag: string, message: string, data?: unknown): void {
    if (!shouldShowLevel('info')) return;
    const header = buildHeader('info', tag, message);
    if (data !== undefined) {
      console.info(`%c${header}`, getStyle('info'), data);
    } else {
      console.info(`%c${header}`, getStyle('info'));
    }
  },

  /**
   * Warn level log
   * @param tag - Source identifier (e.g., service name)
   * @param message - Log message
   * @param data - Optional data
   */
  warn(tag: string, message: string, data?: unknown): void {
    if (!shouldShowLevel('warn')) return;
    const header = buildHeader('warn', tag, message);
    if (data !== undefined) {
      console.warn(`%c${header}`, getStyle('warn'), data);
    } else {
      console.warn(`%c${header}`, getStyle('warn'));
    }
  },

  /**
   * Error level log
   * @param tag - Source identifier (e.g., service name)
   * @param message - Log message
   * @param errorOrData - Optional error or data
   */
  error(tag: string, message: string, errorOrData?: unknown): void {
    if (!shouldShowLevel('error')) return;
    const header = buildHeader('error', tag, message);
    if (errorOrData !== undefined) {
      console.error(`%c${header}`, getStyle('error'), errorOrData);
    } else {
      console.error(`%c${header}`, getStyle('error'));
    }
  },

  /**
   * Cloud Function calls
   * @param name - Function name (e.g., 'getUsers', 'getUserDetails')
   * @param status - Status: 'CALLING', 'SUCCESS', 'ERROR'
   * @param data - Optional response/error data
   * @param duration - Optional duration in ms
   */
  fn(name: string, status: FnStatus, data?: unknown, duration?: number): void {
    if (!shouldShowLevel('debug')) return;

    const statusConfig = {
      CALLING: { emoji: '...', style: 'color: #9C27B0; font-weight: bold' },
      SUCCESS: { emoji: '???', style: 'color: #4CAF50; font-weight: bold' },
      ERROR: { emoji: '???', style: 'color: #EF4444; font-weight: bold' },
    };

    const config = statusConfig[status];
    const durationStr = duration !== undefined ? ` [${formatDuration(duration)}]` : '';
    const header = `??????  FUNCTION ${name} ${config.emoji}${durationStr}`;

    if (data !== undefined) {
      console.log(`%c${header}`, config.style, data);
    } else {
      console.log(`%c${header}`, config.style);
    }
  },

  /**
   * Set log level (also saves to localStorage)
   * @param level - New log level
   */
  setLevel(level: LogLevel): void {
    currentLevel = level;
    try {
      localStorage.setItem(LOG_STORAGE_KEY, JSON.stringify({ level }));
    } catch {
      // Ignore storage errors
    }
    console.info(`%c[Logger] Level set to: ${level}`, 'color: #3B82F6; font-weight: bold');
  },

  /**
   * Get current log level
   */
  getLevel(): LogLevel {
    return currentLevel;
  },
};

// Expose setLevel to window for easy debugging in console
if (typeof window !== 'undefined') {
  (window as unknown as { mlog: { setLevel: (l: LogLevel) => void } }).mlog = {
    setLevel: log.setLevel,
  };
}
